import { FilterOutlineIcon } from '@ikyucom/design-system-icons-react'
import { useCallback, useRef } from 'react'
import { PcFooter } from '~/components/footer/pc'
import { PcHeader } from '~/components/header/pc/PcHeader'
import { SwitchFacetProvider } from '~/features/facets/applied'
import { preferences, tagToFacet } from '~/features/search/criteria'
import { SearchModeProvider } from '~/features/search/criteria/context'
import { SearchBox } from '~/features/search/pc/SearchBox'
import { FacetCheckBox } from '~/features/search/sd/preferences/FacetCheckBox'
import { ppgFacets } from '~/features/search/sd/preferences/PopularPreferences'
import { useOpen } from '~/features/search/sd/preferences/state'
import {
  afternoonTea,
  anniversary,
  birthday,
  businessDinner,
} from '~/features/search/sd/preferences/tags'
import type { TopRootProps } from '~/routes/_index'
import { css } from '~/styled-system/css'
import { flex, hstack, vstack } from '~/styled-system/patterns'
import { Announce } from '../Announce'
import { Areas } from '../Areas'
import { BackToYahooTop } from '../BackToYahooTop'
import { Criteria } from '../Criteria'
import { FooterBanners } from '../FooterBanners'
import { Genre } from '../Genre'
import { RecommendedRestaurants } from '../RecommendedRestaurants'
import { SpecialFeaturesAndCampaigns } from '../SpecialFeaturesAndCampaigns'
import { TopBanner } from '../TopBanner'
import { PopularityRestaurants } from './PopularityRestaurant'

export function PcTopRoot({ data }: TopRootProps) {
  const submitRef = useRef<HTMLButtonElement>(null)
  return (
    <>
      <BackToYahooTop isFromYahooTop={data.isFromYahooTop} />
      <PcHeader />
      <div className={css({ spaceY: '8', marginBottom: '16' })}>
        <TopBanner banners={data.topBanners.banners} />
        <div
          className={vstack({
            alignItems: 'stretch',
            mx: 'auto',
            w: 'content.base',
            gap: '8',
          })}
        >
          <div className={css({ spaceY: '5' })}>
            <div className={css({ rounded: 'md', shadow: 'outline.lg', p: '8' })}>
              <SearchModeProvider mode="save-only">
                <SwitchFacetProvider>
                  <SearchBox ref={submitRef}>
                    <BottomContents />
                  </SearchBox>
                </SwitchFacetProvider>
              </SearchModeProvider>
            </div>
            <Announce />
          </div>
          <div className={css({ spaceY: '12' })}>
            <RecommendedRestaurants data={data.recommendedRestaurants} />
            <Areas />
            <Genre genre={data.genre} />
            <Criteria criteria={data.criteriaContents} />
            <SpecialFeaturesAndCampaigns
              specialFeaturesAndCampaigns={data.topBanners.specialFeaturesAndCampaigns}
            />
            <PopularityRestaurants />
            <FooterBanners />
          </div>
        </div>
      </div>
      <PcFooter type="full" />
    </>
  )
}

function BottomContents() {
  const open = useOpen()
  const onClick = useCallback(() => {
    open('OTHERS')
  }, [open])
  return (
    <div className={flex({ gap: '5' })}>
      <div className={flex({ gap: '4', isolation: 'isolate' })}>
        {bottomPreferences.map((facet) => (
          <FacetCheckBox key={facet.facetKey} facet={facet} hitCountHidden={true} />
        ))}
      </div>
      <button
        type="button"
        onClick={onClick}
        className={hstack({ gap: '2', cursor: 'pointer', _hover: { opacity: '75' } })}
      >
        <FilterOutlineIcon
          className={css({
            borderWidth: 'thin',
            borderColor: 'primary',
            height: '[28px]',
            width: '[28px]',
            rounded: 'full',
            padding: '1',
            color: 'primary',
          })}
        />
        <p>絞り込み</p>
      </button>
    </div>
  )
}

const ikyuBottomPreferences = [
  tagToFacet(anniversary),
  tagToFacet(birthday),
  tagToFacet(businessDinner),
  preferences.bothPrivateRoom,
  preferences.nonSmoking,
  preferences.preSchoolers,
  preferences.freeFlow,
  tagToFacet(afternoonTea),
]

const bottomPreferences = import.meta.env.VITE_MODE === 'ppg' ? ppgFacets : ikyuBottomPreferences
